import cx from 'classnames'
import { LazyMotion, m, AnimatePresence } from 'motion/react'
import {
  GridList,
  GridListItem,
  ListBox,
  ListBoxItem,
  type Selection,
} from 'react-aria-components'
import { Checkbox } from '@/components/inputs/Checkbox'
import { getScrollParent, useId } from '@react-aria/utils'
import { type ReactNode, useEffect, useRef } from 'react'
import styles from './AvailabilitySelector.module.css'
import { ContentFader } from '@/components/ContentFader'
import { getScrollLeftForCentering } from '@/utils/browser-util'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

export interface AvailableDaysListProps {
  children: ReactNode
  selectedKey: number
  onChange?: (key: Selection) => void
  top?: number
  testId?: string
}

/**
 * List of available days
 */
export function AvailableDaysList({
  onChange,
  selectedKey,
  children,
  top,
  testId,
}: AvailableDaysListProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // When a day becomes active, scroll it into view.
  useEffect(() => {
    const listElement = listRef.current
    if (!listElement) {
      return
    }

    const selectedItemElement = listElement.querySelector(
      `.${styles.availableDayTab}[data-selected=true]`,
    )

    if (!selectedItemElement) {
      return
    }

    // The scroll container is the content fader.
    const parent = getScrollParent(selectedItemElement)

    if (parent.scrollWidth === parent.clientWidth) {
      // Not overflowing.
      return
    }

    // Calculate the new "scroll left", ensuring the active
    // day item scrolls to be centered.
    // NOTE: We're not using `scrollIntoView` because it scrolls
    // the entire viewport, not just the scrolling element.
    const newScrollLeft = getScrollLeftForCentering(parent, selectedItemElement)

    const frame = requestAnimationFrame(() => {
      parent.scrollLeft = newScrollLeft
    })
    return () => cancelAnimationFrame(frame)
  }, [selectedKey])

  return (
    <LazyMotion features={loadMotionFeatures}>
      <div className={styles.availableDaysOuter} data-test={testId}>
        <ContentFader>
          <GridList
            aria-label="Available days"
            selectionMode="single"
            disallowEmptySelection
            layout="grid"
            selectedKeys={new Set([selectedKey])}
            onSelectionChange={(selection) => onChange?.(selection)}
            className={styles.availableDaysList}
            ref={listRef}
            style={top !== undefined ? { top } : undefined}
          >
            {children}
          </GridList>
        </ContentFader>
      </div>
    </LazyMotion>
  )
}

export interface AvailableDayProps {
  id: number
  month: string
  day: string
  hasTimesSelected: boolean
  testId?: string
}

/**
 * Available day item
 */
export function AvailableDay({
  id,
  month,
  day,
  hasTimesSelected,
  testId,
}: AvailableDayProps) {
  return (
    <GridListItem
      id={id}
      textValue={`${day} ${month}`}
      data-selected
      data-test={testId}
      className={({ isFocusVisible }) =>
        cx(styles.availableDayTab, isFocusVisible && styles.focusVisible)
      }
    >
      {({ isSelected }) => (
        <>
          <div className={styles.dayNumber}>{day}</div>
          <div className={styles.dayName}>{month}</div>
          <AnimatePresence>
            {hasTimesSelected && (
              <m.div
                className={styles.indicator}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{
                  type: 'spring',
                  stiffness: 700,
                  damping: 40,
                }}
              />
            )}
          </AnimatePresence>
          {isSelected && (
            <m.div
              className={styles.activeIndicator}
              layoutId="indicator"
              transition={{
                type: 'spring',
                stiffness: 700,
                damping: 40,
              }}
            />
          )}
        </>
      )}
    </GridListItem>
  )
}

export interface AvailableTimesListProps {
  header: string
  children: ReactNode
  selectedKeys: Set<string>
  onChange?: (keys: Selection) => void
  spacingX?: 'default' | 'none'
  testId?: string
}

/**
 * List of available times
 */
export function AvailableTimesList({
  header,
  selectedKeys,
  children,
  onChange,
  spacingX = 'default',
  testId,
}: AvailableTimesListProps) {
  const headerId = useId()

  return (
    <div
      className={cx(
        styles.availableTimesContainer,
        spacingX === 'none' && styles.spacingXNone,
      )}
      data-test={testId}
    >
      <div id={headerId} className={styles.availableTimeHeader}>
        {header}
      </div>
      <ListBox
        aria-labelledby={headerId}
        selectionMode="multiple"
        selectedKeys={new Set(selectedKeys)}
        disallowEmptySelection={false}
        onSelectionChange={(selection) => onChange?.(selection)}
      >
        {children}
      </ListBox>
    </div>
  )
}

export interface AvailableTimeProps {
  id: string
  startTime: string
  endTime: string
  testId?: string
}

/**
 * Available time item
 */
export function AvailableTime({
  id,
  startTime,
  endTime,
  testId,
}: AvailableTimeProps) {
  const textValue = `${startTime} - ${endTime}`

  return (
    <ListBoxItem
      id={id}
      textValue={textValue}
      data-test={testId}
      className={({ isSelected, isFocusVisible }) =>
        cx(
          styles.availableTimeItem,
          isSelected && styles.selected,
          isFocusVisible && styles.focusVisible,
        )
      }
    >
      {({ isSelected }) => (
        <>
          <Checkbox checked={isSelected} readOnly isPadded />
          <span className={styles.availableTimeText}>{textValue}</span>
        </>
      )}
    </ListBoxItem>
  )
}
